import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWifi, faVideo, faUser, faBox, faXmark, faCheck } from '@fortawesome/free-solid-svg-icons';
import CameraViewer from '../../../components/camera_viewer/camera_viewer';

const CONFIDENCE_WARN = 0.5;

/**
 * IndoorFloorMap
 *
 * Coordinates (pos_x, pos_y 0–100) are always IMAGE-relative percentages,
 * meaning 0,0 is the top-left corner of the image content and 100,100 is
 * the bottom-right corner, regardless of any letterboxing caused by
 * objectFit:contain in the HTML container or the GroundOverlay aspect ratio.
 *
 * This ensures what you place here appears at the same relative position
 * on the Google Maps GroundOverlay.
 */
export default function IndoorFloorMap({
    floor,
    beacons = [],
    cameras = [],
    positions = {},
    indoorPeople = [],
    gpsAssets = [],
    onPersonClick,
    editMode,
    onPlaceItem,
    pendingPlacement,
    setPendingPlacement,
    onReposition,
    onRemove,
}) {
    const { t } = useTranslation();
    const containerRef = useRef(null);
    const imgRef = useRef(null);
    const [imageLoaded, setImageLoaded] = useState(false);

    const [beaconPopup, setBeaconPopup] = useState(null);
    const [cameraPopup, setCameraPopup] = useState(null);
    const [personPopup, setPersonPopup] = useState(null);

    // Drag via window-level events (works even outside the container)
    const dragItemRef = useRef(null);
    const dragMovedRef = useRef(false);
    const justDraggedRef = useRef(false);
    const onRepositionRef = useRef(onReposition);
    const [dragPos, setDragPos] = useState(null);
    const [draggingId, setDraggingId] = useState(null);

    useEffect(() => { onRepositionRef.current = onReposition; }, [onReposition]);

    // ── Image layout helpers ──────────────────────────────────────────────────
    /**
     * Returns the actual bounds of the image within the container
     * after objectFit:contain letterboxing.
     * { ox, oy, rw, rh, cw, ch }
     *   ox/oy = pixel offset from container top-left to image top-left
     *   rw/rh = rendered image pixel size
     *   cw/ch = container pixel size
     */
    const computeImageLayout = useCallback(() => {
        const container = containerRef.current;
        const img = imgRef.current;
        if (!container || !img || !img.naturalWidth || !img.naturalHeight) return null;

        const cw = container.clientWidth;
        const ch = container.clientHeight;
        if (!cw || !ch) return null;

        const iw = img.naturalWidth;
        const ih = img.naturalHeight;
        const scale = Math.min(cw / iw, ch / ih);
        const rw = iw * scale;
        const rh = ih * scale;
        const ox = (cw - rw) / 2;   // horizontal letterbox offset
        const oy = (ch - rh) / 2;   // vertical letterbox offset

        return { ox, oy, rw, rh, cw, ch };
    }, []);

    /** Convert client mouse position → image-relative percent (0–100) */
    const clientToImagePercent = useCallback((clientX, clientY) => {
        const container = containerRef.current;
        if (!container) return null;
        const rect = container.getBoundingClientRect();
        const layout = computeImageLayout();

        if (!layout) {
            // Image not loaded yet — fallback to container-relative
            return {
                pos_x: parseFloat(Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100)).toFixed(2)),
                pos_y: parseFloat(Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100)).toFixed(2)),
            };
        }

        const relX = clientX - rect.left - layout.ox;
        const relY = clientY - rect.top - layout.oy;
        return {
            pos_x: parseFloat(Math.max(0, Math.min(100, (relX / layout.rw) * 100)).toFixed(2)),
            pos_y: parseFloat(Math.max(0, Math.min(100, (relY / layout.rh) * 100)).toFixed(2)),
        };
    }, [computeImageLayout]);

    /** Convert image-relative percent → CSS left/top for positioning within container */
    const imagePercentToCSS = useCallback((pos_x, pos_y) => {
        const layout = computeImageLayout();
        if (!layout) return { left: `${pos_x}%`, top: `${pos_y}%` };

        const px = layout.ox + (pos_x / 100) * layout.rw;
        const py = layout.oy + (pos_y / 100) * layout.rh;
        return {
            left: `${(px / layout.cw) * 100}%`,
            top: `${(py / layout.ch) * 100}%`,
        };
    }, [computeImageLayout]);

    // ── Window-level drag ────────────────────────────────────────────────────
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!dragItemRef.current) return;
            dragMovedRef.current = true;
            const coords = clientToImagePercent(e.clientX, e.clientY);
            if (coords) setDragPos(coords);
        };

        const handleMouseUp = (e) => {
            if (!dragItemRef.current) return;
            const { type, item } = dragItemRef.current;
            if (dragMovedRef.current) {
                const coords = clientToImagePercent(e.clientX, e.clientY);
                if (coords) {
                    onRepositionRef.current?.(type, item, coords);
                    justDraggedRef.current = true;
                    setTimeout(() => { justDraggedRef.current = false; }, 100);
                }
            }
            dragItemRef.current = null;
            dragMovedRef.current = false;
            setDraggingId(null);
            setDragPos(null);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [clientToImagePercent]);

    const startDrag = useCallback((e, type, item) => {
        if (editMode) return;
        e.preventDefault();
        e.stopPropagation();
        dragItemRef.current = { type, item };
        dragMovedRef.current = false;
        setDraggingId(`${type}-${item.id}`);
        setBeaconPopup(null);
        setCameraPopup(null);
    }, [editMode]);

    // Click on the container background → placement
    const handleContainerClick = useCallback((e) => {
        if (!editMode) return;
        const coords = clientToImagePercent(e.clientX, e.clientY);
        if (coords) setPendingPlacement(coords);
    }, [editMode, clientToImagePercent, setPendingPlacement]);

    // Merge MQTT real-time positions with API snapshot
    const mergedPeople = indoorPeople.map(ip => {
        const rt = positions[ip.device_user];
        if (rt && rt.floor_id === floor?.id) {
            return { ...ip, position_x: rt.position_x, position_y: rt.position_y, confidence: rt.confidence };
        }
        return ip;
    });

    if (!floor?.floor_plan_url) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94A3B8', fontSize: '14px', flexDirection: 'column', gap: '8px' }}>
                <FontAwesomeIcon icon={faBox} style={{ fontSize: '32px' }} />
                <span>{t('gps.indoor.noFloorPlan')}</span>
            </div>
        );
    }

    const isDraggingAny = draggingId !== null;

    return (
        <div
            ref={containerRef}
            onClick={handleContainerClick}
            style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                background: '#1E293B',
                borderRadius: '8px',
                overflow: 'hidden',
                cursor: editMode ? 'crosshair' : (isDraggingAny ? 'grabbing' : 'default'),
                userSelect: 'none',
            }}
        >
            {/* Floor plan image */}
            <img
                ref={imgRef}
                src={floor.floor_plan_url}
                alt={floor.name}
                onLoad={() => setImageLoaded(true)}
                style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', pointerEvents: 'none' }}
                draggable={false}
            />

            {/* Only render markers after image is loaded so layout is correct */}
            {imageLoaded && (
                <>
                    {/* Beacons */}
                    {beacons.map(fb => {
                        const isThis = draggingId === `beacon-${fb.id}`;
                        const pos = isThis && dragPos ? dragPos : { pos_x: fb.position_x, pos_y: fb.position_y };
                        const css = imagePercentToCSS(pos.pos_x, pos.pos_y);
                        return (
                            <div
                                key={`b-${fb.id}`}
                                style={{
                                    position: 'absolute',
                                    left: css.left, top: css.top,
                                    transform: 'translate(-50%, -50%)',
                                    cursor: editMode ? 'crosshair' : (isThis ? 'grabbing' : 'grab'),
                                    zIndex: isThis ? 20 : 5,
                                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (!justDraggedRef.current) { setBeaconPopup(fb); setCameraPopup(null); }
                                }}
                                onMouseDown={(e) => startDrag(e, 'beacon', fb)}
                            >
                                <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#4F46E5', border: '2px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(79,70,229,0.5)' }}>
                                    <FontAwesomeIcon icon={faWifi} color="white" style={{ fontSize: '12px' }} />
                                </div>
                                <div style={{ fontSize: 9, color: '#fff', fontWeight: 700, textAlign: 'center', marginTop: 2, whiteSpace: 'nowrap', textShadow: '0 0 4px #000, 0 0 4px #000' }}>
                                    {fb.beacon_name}
                                </div>
                            </div>
                        );
                    })}

                    {/* Cameras */}
                    {cameras.map(fc => {
                        const isThis = draggingId === `camera-${fc.id}`;
                        const pos = isThis && dragPos ? dragPos : { pos_x: fc.position_x, pos_y: fc.position_y };
                        const css = imagePercentToCSS(pos.pos_x, pos.pos_y);
                        return (
                            <div
                                key={`c-${fc.id}`}
                                style={{
                                    position: 'absolute',
                                    left: css.left, top: css.top,
                                    transform: 'translate(-50%, -50%)',
                                    cursor: editMode ? 'crosshair' : (isThis ? 'grabbing' : 'grab'),
                                    zIndex: isThis ? 20 : 5,
                                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (!justDraggedRef.current) { setCameraPopup(fc); setBeaconPopup(null); }
                                }}
                                onMouseDown={(e) => startDrag(e, 'camera', fc)}
                            >
                                <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#0F766E', border: '2px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(15,118,110,0.5)' }}>
                                    <FontAwesomeIcon icon={faVideo} color="white" style={{ fontSize: '12px' }} />
                                </div>
                                <div style={{ fontSize: 9, color: '#fff', fontWeight: 700, textAlign: 'center', marginTop: 2, whiteSpace: 'nowrap', textShadow: '0 0 4px #000, 0 0 4px #000' }}>
                                    {fc.camera_name}
                                </div>
                            </div>
                        );
                    })}

                    {/* People */}
                    {mergedPeople.map(ip => {
                        const css = imagePercentToCSS(ip.position_x, ip.position_y);
                        return (
                            <div
                                key={`p-${ip.device_user}`}
                                style={{
                                    position: 'absolute',
                                    left: css.left, top: css.top,
                                    transform: 'translate(-50%, -50%)',
                                    cursor: 'pointer', zIndex: 10,
                                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                                }}
                                onClick={(e) => { e.stopPropagation(); const asset = gpsAssets.find(a => a.id === ip.device_user); setPersonPopup({ ip, asset }); setBeaconPopup(null); setCameraPopup(null); onPersonClick?.(ip); }}
                            >
                                <div style={{ width: 32, height: 32, borderRadius: '50%', background: ip.confidence < CONFIDENCE_WARN ? '#F59E0B' : '#16A34A', border: '2px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
                                    <FontAwesomeIcon icon={ip.is_human ? faUser : faBox} color="white" style={{ fontSize: '12px' }} />
                                </div>
                                <div style={{ fontSize: 9, color: '#fff', fontWeight: 700, textAlign: 'center', marginTop: 2, whiteSpace: 'nowrap', textShadow: '0 0 4px #000, 0 0 4px #000' }}>
                                    {ip.device_user_name}
                                </div>
                            </div>
                        );
                    })}

                    {/* Pending placement indicator */}
                    {editMode && pendingPlacement && (() => {
                        const css = imagePercentToCSS(pendingPlacement.pos_x, pendingPlacement.pos_y);
                        return (
                            <div style={{
                                position: 'absolute', left: css.left, top: css.top,
                                transform: 'translate(-50%, -50%)',
                                width: 36, height: 36,
                                border: `3px dashed ${editMode === 'beacon' ? '#4F46E5' : '#0F766E'}`,
                                borderRadius: '50%', pointerEvents: 'none', zIndex: 15,
                            }} />
                        );
                    })()}
                </>
            )}

            {/* Edit mode hint */}
            {editMode && !pendingPlacement && (
                <div style={{
                    position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)',
                    background: 'rgba(30,41,59,0.9)', color: '#fff', borderRadius: '6px', padding: '6px 14px',
                    fontSize: '12px', pointerEvents: 'none', zIndex: 10, whiteSpace: 'nowrap',
                }}>
                    Click on the floor plan to place the {editMode}
                </div>
            )}

            {/* Confirm placement bar */}
            {editMode && pendingPlacement && (
                <div style={{
                    position: 'absolute', bottom: '12px', left: '50%', transform: 'translateX(-50%)',
                    background: '#1E293B', color: '#fff', borderRadius: '8px', padding: '8px 16px',
                    display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', zIndex: 10,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.4)', whiteSpace: 'nowrap',
                }}>
                    <span>X: {pendingPlacement.pos_x.toFixed(1)}% &nbsp; Y: {pendingPlacement.pos_y.toFixed(1)}%</span>
                    <button
                        onClick={(e) => { e.stopPropagation(); onPlaceItem?.(pendingPlacement); }}
                        style={{ background: '#22C55E', border: 'none', borderRadius: '6px', padding: '4px 12px', color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '5px' }}
                    >
                        <FontAwesomeIcon icon={faCheck} /> Confirm
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); setPendingPlacement(null); }}
                        style={{ background: '#EF4444', border: 'none', borderRadius: '6px', padding: '4px 10px', color: '#fff', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>
            )}

            {/* Hint when items exist and not in edit mode */}
            {!editMode && imageLoaded && (beacons.length + cameras.length > 0) && !isDraggingAny && !beaconPopup && !cameraPopup && (
                <div style={{ position: 'absolute', bottom: '8px', right: '10px', fontSize: '10px', color: 'rgba(255,255,255,0.5)', pointerEvents: 'none' }}>
                    Drag to reposition · Click for info
                </div>
            )}

            {/* Beacon popup */}
            {beaconPopup && !isDraggingAny && (
                <BeaconPopup beacon={beaconPopup} onClose={() => setBeaconPopup(null)} t={t}
                    onRemove={onRemove ? () => { onRemove('beacon', beaconPopup.id); setBeaconPopup(null); } : null} />
            )}

            {/* Camera popup */}
            {cameraPopup && !isDraggingAny && (
                <CameraPopupOverlay camera={cameraPopup} onClose={() => setCameraPopup(null)} t={t}
                    onRemove={onRemove ? () => { onRemove('camera', cameraPopup.id); setCameraPopup(null); } : null} />
            )}

            {/* Person popup */}
            {personPopup && !isDraggingAny && (
                <PersonPopup ip={personPopup.ip} asset={personPopup.asset} onClose={() => setPersonPopup(null)} />
            )}
        </div>
    );
}

// ── Popups ───────────────────────────────────────────────────────────────────

function BeaconPopup({ beacon, onClose, onRemove, t }) {
    return (
        <div style={{
            position: 'absolute', top: '16px', right: '16px', zIndex: 50,
            background: '#fff', borderRadius: '10px', padding: '16px', minWidth: '220px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.25)', border: '1px solid #E2E8F0',
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ fontWeight: '700', fontSize: '14px', color: '#1E293B', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FontAwesomeIcon icon={faWifi} style={{ color: '#4F46E5' }} />
                    {beacon.beacon_name}
                </div>
                <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '16px' }}>
                    <FontAwesomeIcon icon={faXmark} />
                </button>
            </div>
            <div style={{ fontSize: '12px', color: '#475569' }}>
                <BeaconRow label={t('gps.indoor.macAddress')} value={beacon.beacon_mac} mono />
                <BeaconRow label={t('gps.indoor.rssiAt1m')} value={`${beacon.rssi_at_1m} dBm`} />
                <BeaconRow label={t('gps.indoor.txPower')} value={`${beacon.tx_power} dBm`} />
                <BeaconRow label={t('gps.indoor.txInterval')} value={`${beacon.tx_interval} ms`} />
                {beacon.description && <BeaconRow label="Description" value={beacon.description} />}
                <BeaconRow label="X / Y" value={`${beacon.position_x}% / ${beacon.position_y}%`} />
            </div>
            {onRemove && (
                <button onClick={onRemove} style={{ marginTop: '12px', width: '100%', background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', borderRadius: '6px', padding: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                    Remove from floor
                </button>
            )}
        </div>
    );
}

function BeaconRow({ label, value, mono }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #F1F5F9' }}>
            <span style={{ color: '#94A3B8', fontWeight: '500' }}>{label}</span>
            <span style={{ fontWeight: '600', color: '#1E293B', fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</span>
        </div>
    );
}

function PersonPopup({ ip, asset, onClose }) {
    const battery = asset?.active_gps_device?.battery != null ? Math.round(asset.active_gps_device.battery) : null;
    const lastMsg = asset?.active_gps_device?.last_message;
    const pos = asset?.current_position;
    return (
        <div style={{
            position: 'absolute', top: '16px', right: '16px', zIndex: 50,
            background: '#fff', borderRadius: '10px', padding: '16px', minWidth: '220px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.25)', border: '1px solid #E2E8F0',
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ fontWeight: '700', fontSize: '14px', color: '#1E293B', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FontAwesomeIcon icon={ip.is_human ? faUser : faBox} style={{ color: ip.is_human ? '#10B981' : '#F59E0B' }} />
                    {ip.device_user_name}
                </div>
                <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '16px' }}>
                    <FontAwesomeIcon icon={faXmark} />
                </button>
            </div>
            <div style={{ fontSize: '12px', color: '#475569' }}>
                <BeaconRow label="Type" value={ip.is_human ? 'Person' : 'Asset'} />
                {asset?.identification_number && <BeaconRow label="Doc" value={asset.identification_number} />}
                {battery != null && <BeaconRow label="Battery" value={`${battery}%`} />}
                {asset?.active_gps_device && <BeaconRow label="Device" value={asset.active_gps_device.name} />}
                {lastMsg && <BeaconRow label="Last msg" value={new Date(lastMsg).toLocaleString()} />}
                <BeaconRow label="Confidence" value={`${Math.round((ip.confidence || 0) * 100)}%`} />
            </div>
            {pos && (
                <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${pos.lat},${pos.lng}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ display: 'block', marginTop: '12px', padding: '6px 0', textAlign: 'center', background: '#1E293B', color: '#fff', borderRadius: '6px', fontSize: '12px', fontWeight: '600', textDecoration: 'none' }}
                >
                    Go to location
                </a>
            )}
        </div>
    );
}

function CameraPopupOverlay({ camera, onClose, onRemove, t }) {
    return (
        <div style={{
            position: 'absolute', top: '16px', right: '16px', zIndex: 50,
            background: '#fff', borderRadius: '10px', padding: '16px', minWidth: '340px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.25)', border: '1px solid #E2E8F0',
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <div style={{ fontWeight: '700', fontSize: '14px', color: '#1E293B', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FontAwesomeIcon icon={faVideo} style={{ color: '#0F766E' }} />
                    {camera.camera_name}
                    {camera.machine_name && (
                        <span style={{ fontSize: '11px', color: '#64748B', fontWeight: '400' }}>({camera.machine_name})</span>
                    )}
                </div>
                <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '16px' }}>
                    <FontAwesomeIcon icon={faXmark} />
                </button>
            </div>
            <CameraViewer cameraId={camera.camera} name={camera.camera_name} camera={camera} />
            {onRemove && (
                <button onClick={onRemove} style={{ marginTop: '10px', width: '100%', background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', borderRadius: '6px', padding: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                    Remove from floor
                </button>
            )}
        </div>
    );
}
